import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Input, Label, Row } from "reactstrap";

import AppModal from "../../../../Components/Common/AppModal";
import {
  twilioRegulatoryBundleDataApi,
  twilioRegulatoryBundleSubmitApi,
} from "../../../../helpers/fakebackend_helper";

// Change #100774 - Twilio will only sell a UK mobile number to a business that
// has been verified (a "regulatory bundle" with that business's own details).
// The company fills this in once; CRMit sends it to Twilio and shows the
// status. Buy a number (mobile) unlocks when Twilio approves.
const STATUS = {
  draft: { label: "Needs correcting", color: "warning" },
  "pending-review": { label: "Under review", color: "info" },
  "in-review": { label: "Under review", color: "info" },
  "provisionally-approved": { label: "Provisionally approved", color: "info" },
  "twilio-approved": { label: "Verified", color: "success" },
  "twilio-rejected": { label: "Rejected - please correct", color: "danger" },
};

const FIELDS = [
  { key: "BusinessName", label: "Registered business name", col: 12 },
  { key: "RegistrationAuthority", label: "Registered with", col: 4, type: "select" },
  { key: "RegistrationNumber", label: "Company registration number", col: 8, help: "Your Companies House number, e.g. 01234567. Filled in from your company details (Registration No.) when set." },
  { key: "BusinessWebsite", label: "Business website", col: 12, help: "Public website or verified social media page" },
  { key: "RepFirstName", label: "Authorised person - first name", col: 6 },
  { key: "RepLastName", label: "Authorised person - last name", col: 6 },
  { key: "RepPhoneNumber", label: "Authorised person - mobile", col: 6, help: "A real mobile they answer, not a virtual/VoIP number" },
  { key: "RepEmail", label: "Authorised person - work email", col: 6, help: "Should be on your business domain" },
  { key: "AddressStreet", label: "Business address line 1", col: 12, help: "Not a PO Box" },
  { key: "AddressStreetSecondary", label: "Address line 2", col: 12, optional: true },
  { key: "AddressCity", label: "Town / city", col: 4 },
  { key: "AddressRegion", label: "County", col: 4 },
  { key: "AddressPostalCode", label: "Postcode", col: 4 },
];

export default function BusinessVerification({ onStatus }) {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [bundle, setBundle] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await twilioRegulatoryBundleDataApi();
      if (res?.status === 200) {
        setAvailable(res.Available !== false);
        setBundle(res.Bundle || null);
        setForm(res.Bundle || res.Prefill || {});
        if (onStatus) onStatus(res.Bundle?.Status || null, res.Available !== false);
      } else if (onStatus) {
        // Older backend without the endpoint: do not block buying.
        onStatus(undefined, false);
      }
    } catch (e) {
      if (onStatus) onStatus(undefined, false);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setProblems([]);
    try {
      const res = await twilioRegulatoryBundleSubmitApi([
        { ...form, IsoCountry: "GB", NumberType: "mobile" },
      ]);
      if (res?.status === 200) {
        toast.success(res.message);
        setOpen(false);
        load();
      } else {
        toast.error(res?.message || "Could not send your details for verification");
        setProblems(res?.Problems || []);
        load();
      }
    } catch (e) {
      toast.error("Could not send your details for verification");
    }
    setSaving(false);
  };

  if (loading) return null;
  if (!available) return null;

  const status = bundle?.Status ? STATUS[bundle.Status] || { label: bundle.Status, color: "secondary" } : null;
  const editable = !bundle || ["draft", "twilio-rejected"].includes(bundle.Status);

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="card-title mb-1">
            Verify your business{" "}
            {status && <span className={`badge bg-${status.color} ms-1 align-middle`}>{status.label}</span>}
          </h5>
          <p className="text-muted mb-0">
            UK mobile numbers (the ones that can text) can only be issued to verified businesses. Do this
            once; it usually takes 1-3 working days.
          </p>
        </div>
        {editable && (
          <button className="btn btn-outline-primary" onClick={() => setOpen(true)}>
            <i className="ri-shield-check-line me-1"></i>
            {bundle ? "Correct and resubmit" : "Verify your business"}
          </button>
        )}
      </CardHeader>
      {bundle?.FailureReason && editable && (
        <CardBody>
          <div className="alert alert-warning mb-0">{bundle.FailureReason.split(" | ").join(" · ")}</div>
        </CardBody>
      )}

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title="Verify your business"
        subtitle="Needed before a UK mobile number can be issued to your business"
        icon="ri-shield-check-line"
        size="md"
        loading={saving}
        onSubmit={submit}
        submitLabel="Submit for verification"
      >
        {problems.length > 0 && (
          <div className="alert alert-danger">
            {problems.map((p) => (
              <div key={p}>{p}</div>
            ))}
          </div>
        )}
        <Row className="g-3">
          {FIELDS.map((f) => (
            <Col md={f.col} key={f.key}>
              <Label className="form-label" htmlFor={`bv-${f.key}`}>
                {f.label}
                {f.optional && <span className="text-muted"> (optional)</span>}
              </Label>
              {f.type === "select" ? (
                <Input
                  id={`bv-${f.key}`}
                  type="select"
                  value={form[f.key] || "UK:CRN"}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                >
                  <option value="UK:CRN">Companies House</option>
                  <option value="OTHER">Other</option>
                </Input>
              ) : (
                <Input
                  id={`bv-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              )}
              {f.help && <small className="text-muted">{f.help}</small>}
            </Col>
          ))}
        </Row>
      </AppModal>
    </Card>
  );
}

BusinessVerification.propTypes = {
  onStatus: PropTypes.func,
};
